const VARIABLES = new Set(['currentIndex', 'cardIndex', 'totalCount', 'feedback', 'testIndex']);

export function getVocabularyCardCoachingContext({ session, isBack = false } = {}) {
  const appearance = session?.current;
  if (!appearance) return null;
  const cardIds = Array.isArray(session.batch?.cardIds) ? session.batch.cardIds : [];
  const batchIndex = cardIds.indexOf(appearance.cardId);
  const rawGrade = Number(appearance.grade);
  const testIndex = Number.isFinite(rawGrade) ? Math.max(0, Math.min(5, Math.floor(rawGrade))) : 0;
  const isLast = batchIndex >= 0 && batchIndex === cardIds.length - 1;
  const isFinalTest = testIndex === 4;
  const leaf = isLast ? 'last' : appearance.firstEncounter === true ? 'first' : isFinalTest ? 'final' : 'default';
  return {
    trigger: isBack ? 'vocabulary.card.back' : `vocabulary.card.front.${leaf}`,
    variables: { currentIndex: (session.history?.length || 0) + 1,
      ...(batchIndex >= 0 ? { cardIndex: batchIndex + 1 } : {}), testIndex },
  };
}

export function getPracticeCardCoachingTrigger({ isFluencyTest = false, isFirst = false, grade = 0, testIndex = 0,
  isBack = false, answerDecisionMade = false, repeatCount = 0 } = {}) {
  // First answer judgement takes priority even after no-flip repetitions.
  if (isBack && !answerDecisionMade) return `practice.card.phase2.${isFluencyTest ? 'fluency' : 'default'}`;
  if (answerDecisionMade || repeatCount > 0) {
    const test = Number(testIndex);
    const fluencyLeaf = test === 4 ? 'fluency.test4' : test === 3 ? 'fluency.test3' : 'fluency';
    return `practice.card.phase3.${isFluencyTest ? fluencyLeaf : isBack ? 'back' : 'front'}`;
  }
  if (isFluencyTest) return 'practice.card.phase1.fluency';
  if (isFirst) return 'practice.card.phase1.first';
  return `practice.card.phase1.${Number(grade) === 2 ? 'grade2' : 'default'}`;
}

export function renderCoachingText(template, variables = {}) {
  if (typeof template !== 'string' || !template.trim()) return null;
  const pattern = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
  if (/\{\{|\}\}/.test(template.replace(pattern, ''))) return null;
  let valid = true;
  const text = template.replace(pattern, (_match, key) => {
    const value = Object.prototype.hasOwnProperty.call(variables, key) ? variables[key] : undefined;
    if (!VARIABLES.has(key) || (key === 'feedback'
      ? typeof value !== 'string' || !value.trim()
      : !Number.isSafeInteger(value) || value < (key === 'testIndex' ? 0 : 1))) {
      valid = false;
      return '';
    }
    return String(value);
  });
  if (Number.isSafeInteger(variables.totalCount) && variables.currentIndex > variables.totalCount) valid = false;
  return valid && text.trim() ? text : null;
}

// The owner supplies the choice scope: a session for ordinary/vocabulary cards,
// a visit for other flows. Trigger and actor choices stay independent.
export function createCoachingSelector(random = Math.random) {
  const choices = new Map();
  const previous = new Map();
  return ({ scenes = [], trigger, visitKey, variables = {}, actor = null }) => {
    let candidates = scenes.filter(scene => scene?.meta?.kind === 'learning-coaching'
      && scene.trigger === trigger && scene.frames?.length === 1
      && renderCoachingText(scene.frames[0].text, variables) !== null);
    if (actor) {
      const exact = candidates.filter(scene => !scene.meta.coachingFallback && scene.frames[0].actor === actor);
      candidates = exact.length ? exact : candidates.filter(scene => scene.meta.coachingFallback === true);
    } else candidates = candidates.filter(scene => !scene.meta.coachingFallback);
    if (!candidates.length) return null;
    const key = JSON.stringify([visitKey, trigger, actor]);
    const saved = candidates.find(scene => scene.id === choices.get(key));
    if (saved) return saved;
    const group = JSON.stringify([trigger, actor]);
    const fresh = candidates.length > 1 ? candidates.filter(scene => scene.id !== previous.get(group)) : candidates;
    const selected = fresh[Math.min(fresh.length - 1, Math.max(0, Math.floor(random() * fresh.length)))];
    choices.set(key, selected.id);
    previous.set(group, selected.id);
    return selected;
  };
}
